import { AuthenticatedAPIClient, type APIResponse } from "./authenticated-api";

/**
 * Tuition-fee integration settings. Admin-only on the backend — `password`
 * comes back in clear so it can be edited without retyping it from memory.
 */
export interface HocPhiConfig {
	/**
	 * Master switch. Off means no lookup reaches the fee system at all —
	 * scheduled run skipped, manual run and probe refused. Distinct from
	 * `HocPhiSchedule.enabled`, which only governs the automatic cadence.
	 */
	enabled: boolean;
	baseUrl: string;
	username: string;
	password: string;
	/** Which source is in effect. Resolution is all-or-nothing, never mixed. */
	source: "db" | "env";
	/** Stored password could not be decrypted — in practice, a rotated secret. */
	passwordUnreadable: boolean;
}

export type HocPhiConfigUpdateInput = Partial<
	Pick<HocPhiConfig, "baseUrl" | "username" | "password">
>;

export interface HocPhiSchedule {
	enabled: boolean;
	cronExpression: string;
}

/**
 * Why lookups failed. These drive completely different responses — call IT,
 * re-enter the password, or do nothing at all — so the screen shows them
 * separately rather than as one "failed" total.
 */
export type HocPhiFailureKind = "notFound" | "auth" | "connection" | "upstream" | "unknown";

export interface HocPhiLastSync {
	status: "running" | "done" | "error" | "cancelled";
	trigger: "manual" | "cron" | "cli";
	startedAt: string;
	finishedAt?: string;
	durationMs?: number;
	processed?: number;
	successful?: number;
	failed?: number;
	/** Scope size, so a progress bar has a denominator. */
	total?: number;
	paid?: number;
	unpaid?: number;
	amountOwedTotal?: number;
	logFile?: string;
	cohortPrefix?: string;
	failures?: Partial<Record<HocPhiFailureKind, number>>;
	/** Set when the run stopped early because the integration looked broken. */
	abortedBy?: HocPhiFailureKind;
	message?: string;
	/** Recorded as running, but old enough that the process is presumed dead. */
	interrupted?: boolean;
}

/** One past run's per-student CSV, as offered for download. */
export interface HocPhiRunFile {
	id: string;
	startedAt: string;
	/**
	 * Last write to the run's CSV. That is the finish time for a run that has
	 * ended; while a run is still in flight it only says how recent its last row
	 * is, so it must not be shown as a finish time then.
	 */
	finishedAt: string;
	rows: number;
	bytes: number;
	/** Looked up, and PAID was written over a different stored status. */
	paid: number;
	/** Looked up, and UNPAID was written over a different stored status. */
	unpaid: number;
	/** Looked up, but the status was already right — nothing to write. */
	skipped: number;
	/** Could not be looked up; the stored status was left alone. */
	failed: number;
}

export interface HocPhiSyncStatus {
	running: boolean;
	lastRun: HocPhiLastSync | null;
}

/** Outcome of a single read-only lookup. Changes no fee status. */
export type HocPhiProbeResult =
	| {
			ok: true;
			mssv: string;
			hasDebt: boolean;
			feeStatus: "PAID" | "UNPAID";
			amountOwed: number | null;
	  }
	| { ok: false; mssv: string; kind: HocPhiFailureKind; message: string };

/**
 * Unwraps the `{ success, message, data }` envelope every `/v1` endpoint
 * returns, throwing `Error(message)` on failure so every caller has one catch
 * shape instead of re-checking `success` everywhere.
 */
async function unwrap<T>(response: Promise<APIResponse<T>>): Promise<T> {
	const result = await response;
	if (!result.success || result.data === undefined) {
		throw new Error(result.message || "Yêu cầu thất bại");
	}
	return result.data;
}

/** Thin typed client for `/v1/hocphi`. No component talks to `fetch` directly. */
class HocPhiApiService {
	private readonly baseUrl = "/hocphi";

	getConfig(): Promise<HocPhiConfig> {
		return unwrap(AuthenticatedAPIClient.get<HocPhiConfig>(`${this.baseUrl}/config`));
	}

	updateConfig(input: HocPhiConfigUpdateInput): Promise<HocPhiConfig> {
		return unwrap(AuthenticatedAPIClient.patch<HocPhiConfig>(`${this.baseUrl}/config`, input));
	}

	/** Master switch. Applies immediately; nothing else has to be saved. */
	setEnabled(enabled: boolean): Promise<HocPhiConfig> {
		return unwrap(
			AuthenticatedAPIClient.patch<HocPhiConfig>(`${this.baseUrl}/enabled`, { enabled }),
		);
	}

	getSchedule(): Promise<HocPhiSchedule> {
		return unwrap(AuthenticatedAPIClient.get<HocPhiSchedule>(`${this.baseUrl}/schedule`));
	}

	updateSchedule(input: HocPhiSchedule): Promise<HocPhiSchedule> {
		return unwrap(AuthenticatedAPIClient.patch<HocPhiSchedule>(`${this.baseUrl}/schedule`, input));
	}

	triggerSync(): Promise<HocPhiSyncStatus> {
		return unwrap(AuthenticatedAPIClient.post<HocPhiSyncStatus>(`${this.baseUrl}/sync`));
	}

	getSyncStatus(): Promise<HocPhiSyncStatus> {
		return unwrap(AuthenticatedAPIClient.get<HocPhiSyncStatus>(`${this.baseUrl}/sync/status`));
	}

	/** Asks a run in progress to stop. Already-written students keep their value. */
	cancelSync(): Promise<{ requested: boolean }> {
		return unwrap(
			AuthenticatedAPIClient.post<{ requested: boolean }>(`${this.baseUrl}/sync/cancel`),
		);
	}

	/** Saves a test lookup's result. The server re-reads the upstream first. */
	applyProbe(mssv: string): Promise<HocPhiProbeResult> {
		return unwrap(
			AuthenticatedAPIClient.post<HocPhiProbeResult>(`${this.baseUrl}/probe/apply`, { mssv }),
		);
	}

	listRuns(): Promise<HocPhiRunFile[]> {
		return unwrap(AuthenticatedAPIClient.get<HocPhiRunFile[]>(`${this.baseUrl}/runs`));
	}

	/**
	 * URL for an `<a download>`, not a fetch: letting the browser stream the file
	 * keeps a multi-megabyte CSV out of JS memory and gives the user the normal
	 * download UI.
	 */
	downloadRunUrl(id: string): string {
		// Same `/v1` prefix AuthenticatedAPIClient uses; the browser sends the
		// session cookie itself, so no auth header is needed here.
		return `/v1${this.baseUrl}/runs/${encodeURIComponent(id)}/download`;
	}

	/** Tests the saved configuration against one student. Writes nothing. */
	probe(mssv: string): Promise<HocPhiProbeResult> {
		return unwrap(
			AuthenticatedAPIClient.post<HocPhiProbeResult>(`${this.baseUrl}/probe`, { mssv }),
		);
	}
}

export const hocphiApi = new HocPhiApiService();
